import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { summariseVat, VatSource, VatSummary } from '../../api/batch8Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Set the financial year end date and choose the source system the VAT files were exported from.',
  'Upload the VAT Return Excel files (Xero file names must look like "Client-VAT Return 01 Aug 2025 - 31 Oct 2025.xlsx").',
  'Click Start Process. The consolidated VAT summary workbook downloads automatically.',
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground disabled:opacity-50';
const money = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function VatAgentPanel({ mission, onBack }: PanelProps) {
  const [yearEnd, setYearEnd] = useState('2025-12-31');
  const [source, setSource] = useState<VatSource>('xero');
  const [useManual, setUseManual] = useState(false);
  const [manualMonth, setManualMonth] = useState('');
  const [manualYear, setManualYear] = useState('2025');
  const [files, setFiles] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<VatSummary>>();

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload at least one .xlsx file.');
      return;
    }
    if (!yearEnd) {
      fail('Choose the financial year end date.');
      return;
    }
    await run(() => summariseVat(files, {
      source,
      financialYearEnd: yearEnd,
      manualQuarterMonth: useManual && manualMonth ? manualMonth : null,
      manualQuarterYear: useManual && manualMonth ? Number(manualYear) || null : null,
    }), 'VAT processing failed.');
  };

  const stats = result?.summary?.stats;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <div className="mb-5 grid gap-4 rounded-lg border border-border bg-muted p-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="vat-ye" className="mb-1.5">
            Financial year end date
          </Label>
          <Input id="vat-ye" type="date" value={yearEnd} onChange={(e) => setYearEnd(e.target.value)} />
        </div>
        <div>
          <p className="mb-1.5 text-sm font-medium text-foreground">Source type</p>
          <div className="flex gap-2">
            {(['xero', 'quickbooks'] as VatSource[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSource(s)}
                className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                  source === s
                    ? 'bg-primary !text-primary-foreground'
                    : 'border border-border bg-background text-foreground hover:bg-accent'
                }`}
              >
                {s === 'xero' ? 'Xero' : 'QuickBooks'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <details className="mb-5 rounded-lg border border-border bg-muted px-4 py-3">
        <summary className="cursor-pointer text-sm font-semibold text-foreground">VAT quarter fallback (optional)</summary>
        <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={useManual} onChange={(e) => setUseManual(e.target.checked)} />
          Use a manual VAT quarter-end month/year when auto-detection fails
        </label>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="vat-qm" className="mb-1.5">
              VAT quarter-end month
            </Label>
            <select id="vat-qm" className={selectClass} disabled={!useManual} value={manualMonth} onChange={(e) => setManualMonth(e.target.value)}>
              <option value="">—</option>
              {MONTHS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">e.g. "Feb" for a Feb/May/Aug/Nov quarter pattern.</p>
          </div>
          <div>
            <Label htmlFor="vat-qy" className="mb-1.5">
              Reference year
            </Label>
            <Input id="vat-qy" type="number" min={2000} max={2100} disabled={!useManual} value={manualYear} onChange={(e) => setManualYear(e.target.value)} />
          </div>
        </div>
      </details>

      <FileDrop
        label="VAT Return Excel files"
        required
        multiple
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Processing…' : 'Start Process'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Processing…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          {stats && (
            <div className="mt-2 text-xs text-green-800">
              <p>
                {stats.total_vat_transactions} VAT transactions · {stats.total_no_vat_transactions} no-VAT transactions · VAT{' '}
                {money(stats.total_vat_amount)} · Net {money(stats.total_net_amount)}
              </p>
              {stats.by_box && (
                <ul className="mt-1 list-disc pl-5">
                  {Object.entries(stats.by_box).map(([box, amount]) => (
                    <li key={box}>
                      {box}: {money(amount)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </SuccessNotice>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload one or more VAT Return .xlsx files to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default VatAgentPanel;
